import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Shield, Droplets, Leaf, Plane, SprayCan, Wind, Gauge, Sparkles } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const flagship = {
  name: "ST-10 Aircraft Exterior Cleaner",
  tagline: "High-performance exterior cleaning engineered for commercial aviation",
  description: "OZO AeroCare ST-10 combines corrosion-inhibiting chemistry, high soil capacity, and environmental responsibility. Certified to both Airbus and Boeing material specifications and formulated specifically for Indian operating conditions.",
  features: [
    {
      icon: Shield,
      title: "Corrosion-Inhibiting Technology",
      points: [
        "Built-in corrosion inhibitors for active surface protection",
        "Safe for composite, metal, and painted aircraft surfaces",
        "Formulated for Indian water hardness variability",
        "Validated through independent material compatibility testing",
      ],
    },
    {
      icon: Droplets,
      title: "High Soil Capacity & Clean Rinse",
      points: [
        "Cuts through heavy aviation-grade grease and grime",
        "Prevents soil re-deposition after application",
        "Residue-free surface finish",
        "Effective single-pass cleaning performance",
      ],
    },
    {
      icon: Leaf,
      title: "Environmentally Sustainable",
      points: [
        "Eco-friendly chemical formulation",
        "Compatible with airport wastewater treatment",
        "Reduces environmental liability for operators",
        "Supports airline ESG and green compliance goals",
      ],
    },
  ],
};

const pipelineProducts = [
  { icon: Wind, name: "Dry Wash Technology", desc: "Waterless aircraft exterior cleaning, critical for water-scarce airports and rapid turnaround operations." },
  { icon: SprayCan, name: "Interior Cleaners & Degreasers", desc: "Cabin-safe formulations for galley, floor, tray tables, and overhead bin surface maintenance." },
  { icon: Sparkles, name: "Cabin Deodorisers", desc: "Persistent odour neutralisation for enhanced passenger comfort and cabin hygiene standards." },
  { icon: Shield, name: "Cabin Disinfectants", desc: "DGCA-compliant disinfection and disinsecticide solutions for cabin interiors and surfaces." },
  { icon: Droplets, name: "Lavatory Maintenance", desc: "High-efficacy formulations for aircraft lavatory systems including odour control and sanitation." },
  { icon: Gauge, name: "Gas Turbine Compressor Cleaning", desc: "Specialised solutions for engine compressor wash procedures and performance restoration." },
];

export default function ServicesSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 sm:py-32 bg-[#F7F4EC]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          className="text-center mb-16"
        >
          <motion.p variants={fadeUp} className="text-sm uppercase tracking-[0.2em] font-medium text-[#0077D1] mb-4">
            Our Solutions
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.2] font-semibold text-[#0C3A72] mb-4">
            Aviation-Grade Chemical Solutions
          </motion.h2>
          <motion.div variants={fadeUp} className="section-divider mx-auto" />
        </motion.div>

        {/* Flagship Product */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white border border-[#0C3A72]/10 p-8 lg:p-12 mb-16"
        >
          <div className="flex items-center gap-3 mb-2">
            <Plane className="w-5 h-5 text-[#0077D1]" />
            <p className="text-sm uppercase tracking-[0.2em] font-medium text-[#0077D1]">Flagship Product</p>
          </div>
          <h3 className="text-2xl sm:text-3xl tracking-tight leading-[1.3] font-medium text-[#0C3A72] mb-3">
            {flagship.name}
          </h3>
          <p className="text-base leading-relaxed text-[#4A627E] max-w-3xl mb-10">
            {flagship.description}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {flagship.features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.title} className="border-t border-[#0C3A72]/10 pt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#0077D1]/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#0077D1]" />
                    </div>
                    <h4 className="text-lg font-medium text-[#0C3A72]">{feature.title}</h4>
                  </div>
                  <ul className="space-y-2">
                    {feature.points.map((point) => (
                      <li key={point} className="text-sm text-[#4A627E] flex items-start gap-2">
                        <span className="w-1 h-1 bg-[#0077D1] rounded-full mt-2 flex-shrink-0" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Made in India Advantages */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10 pt-8 border-t border-[#0C3A72]/10">
            {[
              { title: "Supply Chain Stability", desc: "No shipping delays or customs holds. Station stock on demand." },
              { title: "Faster Technical Support", desc: "Direct access to formulation team. Same-day response." },
              { title: "Zero Forex Exposure", desc: "INR-denominated pricing. No USD/EUR fluctuations." },
              { title: "Cost Predictability", desc: "Stable long-term supply agreements. No import volatility." },
            ].map((item) => (
              <div key={item.title}>
                <h5 className="text-sm font-semibold text-[#0C3A72] mb-1">{item.title}</h5>
                <p className="text-sm text-[#4A627E]">{item.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Pipeline Products */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex items-center gap-3 mb-8">
            <p className="text-sm uppercase tracking-[0.2em] font-medium text-[#0077D1]">Development Pipeline</p>
            <div className="h-px flex-1 bg-[#0C3A72]/10" />
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {pipelineProducts.map((product, idx) => {
              const Icon = product.icon;
              return (
                <AccordionItem
                  key={product.name}
                  value={`item-${idx}`}
                  className="bg-white border border-[#0C3A72]/10 px-6 data-[state=open]:border-[#0077D1]/30"
                  data-testid={`pipeline-product-${idx}`}
                >
                  <AccordionTrigger className="hover:no-underline py-5">
                    <div className="flex items-center gap-4">
                      <span className="cert-badge text-xs text-[#0077D1]">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <Icon className="w-5 h-5 text-[#0077D1]" />
                      <span className="text-left text-lg font-medium text-[#0C3A72]">
                        {product.name}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-[#4A627E] text-base leading-relaxed pl-16">
                    {product.desc}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
